import { useState } from "react";
import { Plus } from "lucide-react";
import { useSections, useBeats, useCreateBeat, useUpdateBeat, useDeleteBeat } from "../../../lib/presentationQueries";
import { useAutosaveEntity } from "../../../hooks/useAutosaveEntity";
import AutosaveField from "./AutosaveField";
import AutosaveStatus from "./AutosaveStatus";
import ConflictBanner from "./ConflictBanner";
import ConfirmDialog from "./ConfirmDialog";

function BeatCard({ workspaceId, presId, beat, onDeleteRequested }) {
  const updateBeat = useUpdateBeat(workspaceId, presId);
  const auto = useAutosaveEntity({
    initialValues: { title: beat.title || "", body_text: beat.body_text || "" },
    revision: beat.revision,
    onSave: (payload) => updateBeat.mutateAsync({ beatId: beat.beat_id, ...payload }),
  });
  return (
    <div className="border border-ink/15 bg-white/50 rounded-lg p-4 mb-3" data-testid={`message-beat-${beat.beat_id}`}>
      <ConflictBanner conflict={auto.conflict} onKeepMine={auto.keepMineAfterConflict} onUseServer={auto.useServerAfterConflict} />
      <AutosaveField
        value={auto.values.title} onChange={(v) => auto.setField("title", v)}
        placeholder="Beat title (optional)" testId={`message-beat-${beat.beat_id}-title`}
      />
      <AutosaveField
        value={auto.values.body_text} onChange={(v) => auto.setField("body_text", v)}
        placeholder="What do you say here? This is the one canonical version of this text." multiline
        testId={`message-beat-${beat.beat_id}-body`}
      />
      <div className="flex items-center justify-between mt-2">
        <AutosaveStatus status={auto.status} onRetry={auto.retry} testId={`message-beat-${beat.beat_id}-autosave-status`} />
        <button onClick={() => onDeleteRequested(beat.beat_id)} className="text-xs text-ink-soft hover:text-terracotta" data-testid={`message-beat-${beat.beat_id}-delete`}>
          Remove
        </button>
      </div>
    </div>
  );
}

export default function SectionsMessageArea({ workspaceId, presId, activeStructureVersionId }) {
  const { data: sectionsData } = useSections(workspaceId, presId, activeStructureVersionId);
  const sections = sectionsData?.items || [];
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [pendingDeleteBeatId, setPendingDeleteBeatId] = useState(null);
  const sectionId = selectedSectionId || sections[0]?.section_id;
  const { data: beatsData } = useBeats(workspaceId, presId, sectionId);
  const createBeat = useCreateBeat(workspaceId, presId, sectionId);
  const deleteBeat = useDeleteBeat(workspaceId, presId, sectionId);
  const beats = beatsData?.items || [];

  if (sections.length === 0) {
    return <p className="text-ink-soft text-sm" data-testid="presentation-message-area-empty">Add a section in Structure first, then write its message here.</p>;
  }

  return (
    <div data-testid="presentation-message-area">
      <h2 className="font-serif text-xl text-ink mb-4">Sections &amp; Message</h2>
      <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="message-section-picker">
        {sections.map((s) => (
          <button
            key={s.section_id} onClick={() => setSelectedSectionId(s.section_id)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${s.section_id === sectionId ? "border-terracotta bg-terracotta/10 text-terracotta" : "border-ink/15 text-ink-soft hover:border-ink/30"}`}
            data-testid={`message-section-tab-${s.section_id}`}
          >
            {s.title}
          </button>
        ))}
      </div>
      {beats.map((b) => (
        <BeatCard key={b.beat_id} workspaceId={workspaceId} presId={presId} beat={b} onDeleteRequested={setPendingDeleteBeatId} />
      ))}
      {beats.length === 0 && <p className="text-ink-soft text-sm mb-3">No beats in this section yet.</p>}
      <button
        onClick={() => createBeat.mutate({ title: "", body_text: "" })}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-terracotta hover:text-terracotta/80"
        data-testid="message-add-beat"
      >
        <Plus size={15} /> Add a beat
      </button>
      <ConfirmDialog
        open={!!pendingDeleteBeatId}
        onOpenChange={(o) => !o && setPendingDeleteBeatId(null)}
        title="Remove this beat?"
        description="InterAct saves an automatic checkpoint first, so you can always restore it from Version History."
        confirmLabel="Remove"
        onConfirm={() => { deleteBeat.mutate(pendingDeleteBeatId); setPendingDeleteBeatId(null); }}
        testId="message-beat-delete-confirm"
      />
    </div>
  );
}
